import type { PostHog, PostHogConfig } from "posthog-js";
import {
  sanitizeAnalyticsProperties,
  sanitizeAnalyticsUrl,
} from "./productAnalytics";

export function createPostHogConsentController(
  sdk: PostHog,
  config: { publicKey: string; host: string },
  hasConsent: () => boolean,
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">,
  currentUrl = "",
  referrer = "",
) {
  let client: PostHog | undefined;
  function sync() {
    try {
      if (!hasConsent() || !config.publicKey.trim()) {
        client?.opt_out_capturing();
        storage?.removeItem("upfest-analytics-context");
        return;
      }
      if (client) {
        if (client.has_opted_out_capturing()) {
          client.reset(true);
          client.opt_in_capturing({ captureEventName: false });
          try {
            storage?.setItem(
              "upfest-analytics-context",
              JSON.stringify({ distinct_id: client.get_distinct_id() }),
            );
          } catch {
            /* Capture still works without storage. */
          }
          client.capture("$pageview");
        }
        return;
      }
      let context: Record<string, string> = {};
      try {
        const saved: unknown = JSON.parse(
          storage?.getItem("upfest-analytics-context") ?? "{}",
        );
        if (saved && typeof saved === "object") {
          for (const [key, value] of Object.entries(saved)) {
            if (typeof value !== "string") continue;
            if (key === "distinct_id" && /^[a-zA-Z0-9-]{1,100}$/.test(value))
              context[key] = value;
            else if (/^utm_(source|medium|campaign|content|term)$/.test(key))
              context[key] = value;
            else if (key === "$initial_referrer")
              context[key] = sanitizeAnalyticsUrl(value);
          }
        }
      } catch {
        /* Optional browser storage. */
      }
      if (!context.distinct_id) {
        context = { $initial_referrer: sanitizeAnalyticsUrl(referrer) };
        if (currentUrl) {
          const url = new URL(sanitizeAnalyticsUrl(currentUrl));
          for (const [key, value] of url.searchParams) context[key] = value;
        }
      }
      const options: Partial<PostHogConfig> = {
        api_host: config.host.trim() || "https://j.upfest.net",
        person_profiles: "identified_only",
        capture_pageview: "history_change",
        capture_pageleave: false,
        autocapture: false,
        rageclick: false,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        capture_performance: false,
        capture_exceptions: false,
        disable_session_recording: true,
        disable_surveys: true,
        disable_conversations: true,
        disable_product_tours: true,
        disable_external_dependency_loading: true,
        advanced_disable_flags: true,
        persistence: "memory",
        save_referrer: false,
        save_campaign_params: false,
        bootstrap: context.distinct_id
          ? { distinctID: context.distinct_id, isIdentifiedID: false }
          : undefined,
        loaded: (instance) => {
          if (!hasConsent()) {
            instance.opt_out_capturing();
            return;
          }
          instance.register(context);
          try {
            storage?.setItem(
              "upfest-analytics-context",
              JSON.stringify({
                ...context,
                distinct_id: instance.get_distinct_id(),
              }),
            );
          } catch {
            /* Capture still works without storage. */
          }
          sdk.opt_in_capturing({ captureEventName: false });
        },
        before_send: (event) => {
          if (!event || !hasConsent()) return null;
          sanitizeAnalyticsProperties(
            event as unknown as Record<string, unknown>,
          );
          return event;
        },
      };
      client = sdk.init(config.publicKey.trim(), options);
    } catch {
      // Analytics must never interrupt browsing or checkout.
    }
  }
  return { sync, client: () => client };
}
